// Smart Sash Admin Panel - User Management Page (v2)

import { Alert, Button } from '@/components/common';
import {
  UserCredentialsModal,
  UserDeleteModal,
  UserFormModal,
  UserResetPasswordModal,
  UserTable,
} from '@/components/user';
import { useUserManagement } from '@/hooks/useUserManagement';
import React from 'react';

export const UserManagement: React.FC = () => {
  const {
    users,
    institutions,
    pagination,
    isLoading,
    error,
    searchTerm,
    currentPage,
    pageSize,
    pageSizeOptions,
    showModal,
    modalMode,
    showDeleteModal,
    userToDelete,
    showCredentialsModal,
    lastCreatedCredentials,
    showChangePasswordModal,
    userToChangePassword,
    selectedUser,
    formData,
    isSaving,
    unlinkingCardId,
    isAssigningCard,
    setCurrentPage,
    handlePageSizeChange,
    handleSearchChange,
    handleCreateClick,
    handleEditClick,
    handleDeleteClick,
    handleChangePasswordClick,
    handleConfirmChangePassword,
    handleSubmit,
    handleDelete,
    handleInputChange,
    handleUnlinkCard,
    handleAssignCard,
    closeModal,
    closeDeleteModal,
    closeCredentialsModal,
    closeChangePasswordModal,
    clearError,
  } = useUserManagement();

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-0">User Management</h1>
              <p className="text-muted">Manage user accounts and permissions</p>
            </div>
            <Button variant="primary" icon="fas fa-plus" onClick={handleCreateClick}>
              Add User
            </Button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="row mb-3">
          <div className="col-12">
            <Alert variant="danger" dismissible onDismiss={clearError}>
              {error}
            </Alert>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="row">
        <div className="col-12">
          <UserTable
            users={users}
            pagination={pagination}
            searchTerm={searchTerm}
            currentPage={currentPage}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            isLoading={isLoading}
            onSearchChange={handleSearchChange}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onChangePassword={handleChangePasswordClick}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      <UserFormModal
        isOpen={showModal}
        mode={modalMode}
        formData={formData}
        institutions={institutions}
        userCards={selectedUser?.cards || []}
        isSaving={isSaving}
        unlinkingCardId={unlinkingCardId}
        isAssigningCard={isAssigningCard}
        onInputChange={handleInputChange}
        onUnlinkCard={handleUnlinkCard}
        onAssignCard={handleAssignCard}
        onSubmit={handleSubmit}
        onClose={closeModal}
      />

      {/* Delete Confirmation Modal */}
      <UserDeleteModal
        isOpen={showDeleteModal}
        user={userToDelete}
        isSaving={isSaving}
        onConfirm={handleDelete}
        onClose={closeDeleteModal}
      />

      {/* Credentials Display Modal */}
      <UserCredentialsModal
        isOpen={showCredentialsModal}
        credentials={lastCreatedCredentials}
        onClose={closeCredentialsModal}
      />

      {/* Reset Password Modal (confirm → loading → credentials) */}
      <UserResetPasswordModal
        isOpen={showChangePasswordModal}
        user={userToChangePassword}
        credentials={lastCreatedCredentials}
        isLoading={isSaving}
        onConfirm={handleConfirmChangePassword}
        onClose={closeChangePasswordModal}
      />
    </div>
  );
};

export default UserManagement;
